import React, { useRef, useState } from "react";
import { Avatar, Badge, Button, Divider, List, Popover, Tabs, Tooltip, Typography } from "antd";
import { BellOutlined, BellFilled } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

import {
  bp_get_batch_notification_for_employee,
  get_approval_notifications,
  mark_approval_notification_read,
  handleSessionExpired,
} from '@/utils/api';
import {
  APPROVAL_NOTIFICATION_EVENT,
  approvalNotificationInitials,
  buildApprovalNotificationPath,
} from '@/utils/approvalNotificationNav';
import { batchInboxInitials, formatOperationLabel } from '@/utils/batchNotificationDisplay';
import { defaultColors } from '@/themes/antd-theme';
import { useResponsive } from '@/hooks/useResponsive';
import { useTheme } from '@/App';
import { formatDate, formatDateTime } from '@/utils/datetime';
import useLiveNotifications from '@/hooks/useLiveNotifications';

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const Notification = () => {
  const { isDark } = useTheme();
  const { isMobile } = useResponsive();
  // recipe_type sent as string 'true' (Production) / 'false' (Test).
  const recipeTypeFlag = useSelector((state) => state.testProdView) === 'production';
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [notifications_data, set_notifications_data] = useState([]);
  const [alert_data, set_alert_data] = useState([]);
  const [activeTab, setActiveTab] = useState('1');
  // Set on a 401 so subsequent ticks no-op instead of hitting a dead session.
  const stoppedRef = useRef(false);

  const handleToggle = (newOpen) => {
    setOpen(newOpen);
    if (newOpen) {
      bp_get_batch_notification_for_employee(success_get_notification, { recipe_type: recipeTypeFlag });
      get_approval_notifications(success_get_alerts, { recipe_type: recipeTypeFlag });
    }
  };

  const success_get_notification = (res) => {
    if (res?.error) return;
    set_notifications_data(res.data.data);
  };

  const success_get_alerts = (res) => {
    if (res?.error) return;
    set_alert_data(res?.data?.data || []);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === '2' && !alert_data?.length) {
      get_approval_notifications(success_get_alerts, { recipe_type: recipeTypeFlag });
    }
  };

  const handleApprovalClick = (item) => {
    const path = buildApprovalNotificationPath(item);
    const id = item?.notification_id;
    if (id && !item?._legacy && !String(id).startsWith('legacy-')) {
      mark_approval_notification_read(id, () => {
        set_alert_data((prev) =>
          (prev || []).map((row) =>
            row.notification_id === id ? { ...row, is_read: true } : row
          )
        );
      });
    }
    if (path) {
      setOpen(false);
      navigate(path);
    }
  };

  // Wraps the callback-style api.js functions in a Promise so both can be
  // awaited together — and, since they now go through the same dedupeGetRequest
  // as handleToggle/handleTabChange's on-demand fetches, an overlapping poll +
  // manual open shares one in-flight request instead of firing a duplicate.
  const fetchAsPromise = (apiFn, params) => new Promise((resolve) => apiFn(resolve, params));

  useLiveNotifications(async () => {
    if (stoppedRef.current) return;
    // Stop polling + hand a 401 to the single global session-expired handler
    // (redirects to login ONCE, clears storage).
    const stopAndExpire = (msg) => {
      stoppedRef.current = true;
      handleSessionExpired(msg);
    };

    const [alertRes, notifRes] = await Promise.all([
      fetchAsPromise(get_approval_notifications, { recipe_type: recipeTypeFlag }),
      fetchAsPromise(bp_get_batch_notification_for_employee, { recipe_type: recipeTypeFlag }),
    ]);

    if (alertRes?.error) {
      if (alertRes.status === 401) {
        stopAndExpire(alertRes.message);
        return;
      }
    } else {
      set_alert_data(alertRes?.data?.data || []);
    }

    if (notifRes?.error) {
      if (notifRes.status === 401) {
        stopAndExpire(notifRes.message);
        return;
      }
    } else {
      set_notifications_data(notifRes?.data?.data || []);
    }
  }, ['recipe_approval', 'batch_notification', APPROVAL_NOTIFICATION_EVENT], 30000);

  const unreadApprovalCount = (alert_data || []).filter((item) => item?.is_read !== true).length;

  const renderNotificationItem = (item, index) => (
    <React.Fragment key={index}>
      <List.Item className="py-2">
        <div className="flex w-full gap-3">
          <Avatar
            size={36}
            style={{
              color: defaultColors.success,
              backgroundColor: `${defaultColors.success}20`,
              flexShrink: 0
            }}
          >
            {batchInboxInitials(item)}
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2 mb-2">
              <Typography.Paragraph
                ellipsis={{
                  rows: 2,
                  expandable: true,
                  symbol: "more",
                }}
                className="mb-0"
              >
                {item?.msg}
              </Typography.Paragraph>
              <div className="flex flex-col text-xs text-right" style={{ color: defaultColors.text.secondary }}>
                <div>{formatDate(item?.batch_schedule_time || item?.created_at || item?.requested_at)}</div>
                <div>{formatDateTime(item?.batch_schedule_time || item?.created_at || item?.requested_at, 'HH:mm:ss')}</div>
              </div>
            </div>
            <div className="flex flex-col gap-0.5 text-xs" style={{ color: defaultColors.text.secondary }}>
              {item?.recipe_name && <div>Product: {item.recipe_name}</div>}
              {item?.batch_number && <div>Batch: {item.batch_number}</div>}
              {item?.process_name && <div>Process: {item.process_name}</div>}
              {formatOperationLabel(item) && <div>Operation: {formatOperationLabel(item)}</div>}
              {item?.status && <div>Status: {item.status}</div>}
            </div>
          </div>
        </div>
      </List.Item>
      <Divider className="my-0" />
    </React.Fragment>
  );

  const renderAlertItem = (item, index) => (
    <React.Fragment key={item?.notification_id ?? index}>
      <List.Item
        className="py-2 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 rounded"
        onClick={() => handleApprovalClick(item)}
      >
        <div className="flex w-full gap-3">
          <Badge dot={!item?.is_read} offset={[-2, 2]}>
            <Avatar
              size={36}
              style={{
                color: defaultColors.warning,
                backgroundColor: 'white',
                border: `1px solid ${defaultColors.warning}`,
                flexShrink: 0
              }}
            >
              {approvalNotificationInitials(item)}
            </Avatar>
          </Badge>
          <div className="flex-1 min-w-0">
            {item?.title || item?.module ? (
              <div className="text-xs font-semibold mb-1" style={{ color: defaultColors.warning }}>
                {item?.title || item?.module}
              </div>
            ) : null}
            <Typography.Paragraph
              ellipsis={{
                rows: 2,
                expandable: false,
              }}
              className="mb-2"
            >
              {item?.message || item?.msg}
            </Typography.Paragraph>
            <div className="text-xs" style={{ color: defaultColors.text.secondary }}>
              Date: {formatDateTime(item?.created_at || item?.date)}
            </div>
          </div>
        </div>
      </List.Item>
      <Divider className="my-0" />
    </React.Fragment>
  );

  const tabItems = [
    {
      key: '1',
      label: (
        <div className="flex items-center gap-2">
          <BellOutlined style={{ color: defaultColors.success }} />
          <span>Notifications</span>
        </div>
      ),
      children: (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <List
            dataSource={notifications_data?.slice(0, 5)}
            renderItem={renderNotificationItem}
          />
          <Divider />
          <div className="text-center py-3">
            <Button
              type="link"
              onClick={() => {
                navigate("/notifications");
                setOpen(false);
              }}
            >
              <h6 className="text-lg font-semibold m-0" style={{ color: defaultColors.primary }}>
                View All
              </h6>
            </Button>
          </div>
        </div>
      )
    },
    {
      key: '2',
      label: (
        <div className="flex items-center gap-2">
          <Badge count={unreadApprovalCount} size="small">
            <BellFilled style={{ color: defaultColors.warning }} />
          </Badge>
          <span>Approval Notifications</span>
        </div>
      ),
      children: (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <List
            dataSource={alert_data?.slice(0, 5)}
            renderItem={renderAlertItem}
          />
          <Divider />
          <div className="text-center py-3">
            <Button
              type="link"
              onClick={() => {
                navigate("/notifications?tab=approval");
                setOpen(false);
              }}
            >
              <h6 className="text-lg font-semibold m-0" style={{ color: defaultColors.primary }}>
                View All
              </h6>
            </Button>
          </div>
        </div>
      )
    }
  ];

  const popoverContent = (
    <div style={{ width: isMobile ? 285 : 420 }}>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        centered
      />
    </div>
  );

  // Dark mode aware colors
  const bgColor = isDark
    ? (open ? defaultColors.grey[600] : defaultColors.grey[700])
    : (open ? defaultColors.grey[300] : defaultColors.grey[100]);
  const iconColor = isDark ? defaultColors.grey[100] : defaultColors.grey[700];

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={handleToggle}
      placement={isMobile ? "bottom" : "bottomRight"}
    >
      <Tooltip title="Notifications" zIndex={9999}>
        <Button
          type="text"
          shape="circle"
          icon={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Badge count={(notifications_data?.length || 0) + unreadApprovalCount} size="small">
                <BellOutlined style={{ fontSize: 23, display: 'block', color: iconColor }} />
              </Badge>
            </div>
          }
          style={{
            backgroundColor: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        />
      </Tooltip>
    </Popover>
  );
};

export default Notification;
